/**
 * Z1(평론 #6) — **손가락용 공격 버튼(자동 표적).** 폰에서는 몸이 작아 몬스터를 정확히 누르기 어렵고, 전용 공격 버튼이 없어
 * 탭 이동·탭 공격뿐이었다. 모바일 액션 게임처럼 오른쪽 아래(액션 줄 위)에 둥근 [공격] 하나를 둔다.
 *
 * 누르면: 지금 고른 몬스터가 살아 있고 가까우면 그것을, 아니면 내 둘레(9칸)에서 가장 가까운 몬스터를 친다 — 보내는 것은 손가락으로
 * 몬스터를 누른 것과 같은 `attack` 한 줄이고(표적·사거리·주기는 서버가 정한다), 표적 고리·휘두름은 누른 프레임에 먼저 뜬다.
 * 잡던 것이 쓰러졌으면 한 번 더 누르면 다음으로 가까운 것을 잡는다. 둘레에 없으면 버튼이 잠깐 흐려질 뿐 창을 띄우지 않는다(절대 규칙 4).
 *
 * 보이는 때: 손가락 화면(거친 포인터)·사냥 중(안전한 땅이 아니고 살아 있을 때)·창이 닫혀 있을 때. PC 는 마우스로 누른다 — 안 보인다.
 *
 * 묶음 CL2(DESIGN 14.458, 사용자 실보고 「공격 버튼은 무슨 소용인가?」) — **자동 공격이 켜져 있으면 큰 [공격] 은 할 일이 없다.**
 * 서버 자동 공격은 손이 닿는 몬스터(사거리 안)를 이미 알아서 친다. 그래서 자동이 켜진 동안은 작은 **[표적 바꾸기]** 로 바뀐다 —
 * 지금 치는 것 **말고** 둘레(9칸)에서 가까운 다른 몬스터로 표적을 옮긴다(누를 때마다 다음 것 · 4초 안에 거친 것은 뒤로 미룬다).
 * 사거리 밖 몬스터를 고르면 서버가 걸어가서 친다(같은 `attack` 한 줄 — 자동이 사거리 밖을 쫓지 않는 빈칸을 손가락 하나로 메운다).
 * 자동을 끄면(수동) 지금 크기의 [공격] 그대로다.
 * 자리는 **실제로 그려진 HUD 를 재서** 정한다(`attackButtonSpot`) — 짧은 가로 폰에서 오른쪽 위 묶음(미니맵·구역·목표 줄)이 스킬 줄
 * 가까이 내려와, 스킬 줄 위에 고정된 버튼이 목표 줄 「마을 상점에서 물약 사기」를 덮었다(사용자 캡처 844×390). 버튼은 스킬·독 위에
 * 서고, 오른쪽 묶음과 겹치면 그 묶음의 왼쪽으로 비킨다. 목표 줄·경고 카드를 펼친 동안에는 숨는다(펼친 상자가 그 자리를 쓴다).
 */
import { S } from '../core/state.js';
import { send, on } from '../core/net.js';
import { safeGround } from '../core/input.js';
import { predictAttack } from '../render/combat-predict.js';
import { isoOnScreen } from '../render/iso-scene.js';
import { PHONE_HUD_MEDIA, phoneHud } from './landscape-hint.js';

/** 버튼이 표적을 찾는 반경(칸) — 원거리 사거리(5.2)보다 넓게, 화면 안(시야 9칸)으로. */
export const ATTACK_BUTTON_REACH = 9;
/** 수동 [공격] · 자동 중 [표적 바꾸기] 지름(CSS px) — 둘 다 손가락 하한 44px 위 */
export const ATTACK_BUTTON_SIZE = 74;
export const TARGET_SWITCH_SIZE = 56;
/**
 * Q299(사용자 실보고 10-13 갤럭시 가로 — 「UI가 화면 반을 가린다」) — 폰(세로·짧은 가로)에서는 한 단계 작게, 스킬 줄 바로 위에 붙여(간격 4px)
 * 스킬과 한 덩어리로 선다. [표적 바꾸기] 44px 는 손가락 하한(사용자 「최소 40px 안팎」) 위.
 */
export const ATTACK_BUTTON_SIZE_PHONE = 64;
export const TARGET_SWITCH_SIZE_PHONE = 44;
/** [표적 바꾸기] 를 이 시간 안에 다시 누르면 방금 거친 몬스터를 뒤로 미룬다(ms) — A·B 사이만 오가지 않고 C 로 넘어간다 */
const CYCLE_MS = 4000;

/**
 * C6(DESIGN 14.476) — 버튼이 고르는 몬스터는 **화면 안에 그려진 것**만이다(`isoOnScreen` — 지금 그린 자리 `viewX/viewY`). 9칸 원은 세로 폰·가까운
 * 시야 단계에서 화면 밖까지 닿아, 보이지 않는 몬스터로 걸어가 쳤다(사용자 영상 10-04). 서버 자동 공격의 표적은 서버가 고른다(V1b).
 */
const usable = (m, you, reach, onScreen = isoOnScreen) => m && !m.sg && !m.r && !m.d && Math.hypot(m.x - you.x, m.y - you.y) <= reach
  && onScreen(m.viewX ?? m.x, m.viewY ?? m.y);

/** 누를 때 칠 몬스터 — 고른 것이 살아 있고 반경 안이면 그것, 아니면 가장 가까운 것(공성 구조물·복귀 중·쓰러지는 몬스터는 뺀다). */
export function attackButtonTarget(you = S.you, mons = S.mons, sel = S.sel, reach = ATTACK_BUTTON_REACH) {
  if (!you) return null;
  const ok = (m) => usable(m, you, reach);
  const cur = sel != null ? (mons || []).find((m) => m.id === sel) : null;
  if (ok(cur)) return cur;
  let best = null, bd = Infinity;
  for (const m of mons || []) { if (!ok(m)) continue; const d = Math.hypot(m.x - you.x, m.y - you.y); if (d < bd) { bd = d; best = m; } }
  return best;
}

/**
 * CL2 — 자동 중 [표적 바꾸기]: 지금 표적(`current`)이 **아닌** 가장 가까운 몬스터. 방금 거친 것(`recent`)은 뒤로 미루고,
 * 다 거쳤으면 가장 가까운 다른 것. 다른 몬스터가 없으면 null(버튼이 잠깐 흐려진다).
 */
export function nextAttackTarget(you = S.you, mons = S.mons, current = S.sel, recent = [], reach = ATTACK_BUTTON_REACH) {
  if (!you) return null;
  const dist = (m) => Math.hypot(m.x - you.x, m.y - you.y);
  const list = (mons || []).filter((m) => m.id !== current && usable(m, you, reach)).sort((a, b) => dist(a) - dist(b));
  return list.find((m) => !recent.includes(m.id)) || list[0] || null;
}

/**
 * CL2 — 버튼 자리(순수 계산 · 캡처 검사가 같은 함수를 부른다). 사각형은 화면 좌표 {left,top,right,bottom}.
 *   ① 오른쪽 끝(여백 `edge`) 기둥에서, 그 기둥과 가로로 겹치는 아래 조작줄(스킬·독) 위로 띄운다.
 *   ② 그 자리가 오른쪽 위 묶음(`stack` — 미니맵·구역·목표 줄 …)과 겹치면 묶음의 왼쪽으로 비킨다(같은 규칙으로 아래 조작줄 위).
 *   ③ 비킨 자리가 다른 창과 더 많이 겹치면 ①에 남는다 — 화면 밖으로는 안 민다.
 * 돌려주는 값 { right, bottom }(CSS px, 오른쪽·아래 기준).
 */
export function attackButtonSpot({ W, H, size, low = [], stack = null, others = [], edge = 14, gap = 10 }) {
  const hits = (a, b) => a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;
  const at = (right) => {
    const x0 = W - right - size, x1 = W - right;
    let floor = H - 8;
    for (const r of low) if (r && r.left < x1 && r.right > x0) floor = Math.min(floor, r.top - gap);
    return { right, bottom: H - floor, box: { left: x0, right: x1, top: floor - size, bottom: floor } };
  };
  const all = [stack, ...low, ...others].filter(Boolean);
  const score = (s) => all.filter((r) => hits(s.box, r)).length + (s.box.left < 0 || s.box.top < 0 ? 9 : 0);
  const first = at(edge);
  if (!stack || !hits(first.box, stack)) return { right: first.right, bottom: first.bottom };
  const aside = at(Math.max(edge, W - stack.left + gap));
  const pick = score(aside) <= score(first) ? aside : first;
  return { right: pick.right, bottom: pick.bottom };
}

let btn = null;
export function initAttackButton() {
  if (btn || typeof document === 'undefined') return;
  btn = document.createElement('button');
  btn.id = 'b-attack'; btn.type = 'button'; btn.textContent = '공격';
  btn.setAttribute('aria-label', '공격 — 가장 가까운 몬스터를 친다');
  const style = document.createElement('style');
  style.textContent = `#b-attack{position:absolute;right:14px;bottom:calc(var(--barh, 54px) + var(--skillh, 0px) + 14px);z-index:8;display:none;
    width:${ATTACK_BUTTON_SIZE}px;height:${ATTACK_BUTTON_SIZE}px;border-radius:50%;padding:0;font-size:16px;font-weight:700;letter-spacing:1px;color:#fff3dc;
    background:radial-gradient(circle at 50% 38%,#b8462f,#6e2016 70%);border:2px solid #e8b25a;box-shadow:0 4px 16px #000c,inset 0 -3px 0 #0005;
    touch-action:manipulation;-webkit-tap-highlight-color:transparent;user-select:none}
  #b-attack.on{display:block}
  #b-attack:active,#b-attack.hit{transform:scale(.92);background:radial-gradient(circle at 50% 38%,#d8583c,#8a2a1c 70%)}
  #b-attack.none{opacity:.45}
  #b-attack.switch{width:${TARGET_SWITCH_SIZE}px;height:${TARGET_SWITCH_SIZE}px;font-size:var(--ui-note, 12px);font-weight:600;letter-spacing:0;line-height:1.2;
    color:#f4ead2;background:radial-gradient(circle at 50% 38%,#3d5560,#1d2a33 72%);border:2px solid #c9b07a;box-shadow:0 3px 10px #000b,inset 0 -2px 0 #0005}
  #b-attack.switch:active,#b-attack.switch.hit{background:radial-gradient(circle at 50% 38%,#527080,#26363f 72%)}
  @media ${PHONE_HUD_MEDIA}{#b-attack{width:${ATTACK_BUTTON_SIZE_PHONE}px;height:${ATTACK_BUTTON_SIZE_PHONE}px}
    #b-attack.switch{width:${TARGET_SWITCH_SIZE_PHONE}px;height:${TARGET_SWITCH_SIZE_PHONE}px;font-size:11px;line-height:1.15}}`;
  document.head.appendChild(style);
  // CL2 — 게임 화면(#game) 안에 둔다. 전에는 body 에 붙어 #game 의 쌓임 맥락 **위**에 떴다 — 연결 끊김 덮개·첫 입장 이야기 띠
  // (세로 폰에서는 스킬 줄 바로 위, 이 버튼 자리)·열린 창보다 위에 그려져 「건너뛰기」를 덮었다. 이제 그 창들이 버튼 위에 온다.
  (document.getElementById('game') || document.body).appendChild(btn);
  let mode = '';
  const cycle = { ids: [], at: -1e9, last: null };
  const setMode = (next) => {
    if (next === mode) return false;
    mode = next;
    btn.classList.toggle('switch', next === 'switch');
    if (next === 'switch') {
      btn.innerHTML = '표적<br>바꾸기';
      btn.setAttribute('aria-label', '표적 바꾸기 — 지금 치는 몬스터 말고 가까운 다른 몬스터로 (자동 공격 중)');
    } else {
      btn.textContent = '공격';
      btn.setAttribute('aria-label', '공격 — 가장 가까운 몬스터를 친다');
    }
    return true;
  };
  const press = (e) => {
    e.preventDefault(); e.stopPropagation();
    let m;
    if (mode === 'switch') {
      const now = performance.now();
      if (now - cycle.at > CYCLE_MS) cycle.ids = [];
      // 막 누른 직후에는 서버 표적(`S.sel`)이 아직 안 바뀌었을 수 있다 — 0.7초 안이면 방금 고른 것을 지금 표적으로 본다
      const current = cycle.last != null && now - cycle.at < 700 ? cycle.last : S.sel;
      m = nextAttackTarget(S.you, S.mons, current, cycle.ids);
      if (m) {
        if (cycle.ids.includes(m.id)) cycle.ids = []; // 다 거쳤다 — 새 바퀴
        if (current != null && !cycle.ids.includes(current)) cycle.ids.push(current);
        cycle.ids = [...cycle.ids, m.id].slice(-8);
        cycle.at = now; cycle.last = m.id;
      }
    } else m = attackButtonTarget();
    btn.classList.remove('none', 'hit'); void btn.offsetWidth;
    if (!m) { btn.classList.add('none'); setTimeout(() => btn?.classList.remove('none'), 350); return; }
    btn.classList.add('hit'); setTimeout(() => btn?.classList.remove('hit'), 120);
    send({ t: 'attack', id: m.id });
    predictAttack(S.you, m);
  };
  btn.addEventListener('pointerdown', press);
  btn.addEventListener('contextmenu', (e) => e.preventDefault());
  const coarse = () => { try { return matchMedia('(pointer: coarse)').matches; } catch { return false; } };
  const rectOf = (sel) => {
    const el = document.querySelector(sel);
    if (!el || el.hidden) return null;
    const cs = getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden') return null;
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0 ? r : null;
  };
  // 상태 틱(10Hz)마다 재지 않는다 — 0.25초에 한 번, 보이기 시작할 때·모드가 바뀔 때·화면이 바뀔 때는 바로
  let placedAt = -1e9;
  const place = (force) => {
    const now = performance.now();
    if (!force && now - placedAt < 250) return;
    placedAt = now;
    const phone = phoneHud();
    const size = mode === 'switch' ? (phone ? TARGET_SWITCH_SIZE_PHONE : TARGET_SWITCH_SIZE) : (phone ? ATTACK_BUTTON_SIZE_PHONE : ATTACK_BUTTON_SIZE);
    const spot = attackButtonSpot({ W: innerWidth, H: innerHeight, size, gap: phone ? 4 : 10, low: [rectOf('#skills'), rectOf('#actions')],
      stack: rectOf('#rightstack'), others: [rectOf('#topleft'), rectOf('#targetframe'), rectOf('#partyframes'), rectOf('#mainbar')] });
    const right = `${Math.round(spot.right)}px`, bottom = `${Math.round(spot.bottom)}px`;
    if (btn.style.right !== right) btn.style.right = right;
    if (btn.style.bottom !== bottom) btn.style.bottom = bottom;
  };
  const sync = (ev) => {
    const y = S.you;
    // 펼친 목표 줄·경고 카드(짧은 가로 폰에서는 오른쪽 위에 떠서 이 자리까지 내려온다)가 있는 동안은 숨는다
    const unfolded = !!document.querySelector('#goalline.expanded, #crimeself details[open]');
    const show = coarse() && !!y && !(y.dead > 0) && !safeGround(y) && !S.panel && !unfolded;
    const was = btn.classList.contains('on');
    btn.classList.toggle('on', show);
    if (!show) return;
    const changed = setMode(y.auto ? 'switch' : 'attack');
    place(!was || changed || ev?.type === 'resize');
  };
  on('state', sync);
  addEventListener('unige:panel-opened', sync);
  addEventListener('resize', sync);
  sync();
}
